import type { ComponentProps } from "react";
import { PageHeader } from "@/components/page-header";
import { OffersSkeleton } from "@/components/page-skeletons";
import { ViewFallback } from "@/components/page-states";
import { OffersManager } from "@/components/settings/offers-manager";
import { WorkspaceBanner } from "@/components/workspace-banner";
import { useT } from "@shared/client/locale";
import { usePageTitle, useView } from "@shared/client/view";

type OffersData = ComponentProps<typeof OffersManager> & { view: { clientName: string } | null };

/**
 * « Mes offres » : ce que l'agent a compris de chaque offre, qui il doit
 * viser, et à quel rythme il repasse. Atteignable depuis la carte de veille,
 * depuis le sélecteur d'offre et depuis une fiche lead qu'on écarte pour cause
 * de mauvais ciblage.
 */
export default function OffersPage() {
  const t = useT().settings.offers;
  const { data, error } = useView<OffersData>("/api/views/offers");
  const many = Boolean(data && data.offers.length > 1);
  const title = many ? t.titleMany : t.titleOne;
  usePageTitle(many ? t.documentTitleMany : t.documentTitleOne);
  if (!data) return <ViewFallback error={error} skeleton={<OffersSkeleton />} subject={t.loadSubject} />;

  return (
    <div className="max-w-[920px]">
      <PageHeader title={title} />
      {data.view && (
        <WorkspaceBanner>
          {t.workspace.before}
          <strong>{data.view.clientName}</strong>
          {t.workspace.after}
        </WorkspaceBanner>
      )}
      <OffersManager
        activeOfferId={data.activeOfferId}
        offers={data.offers}
        plan={data.plan}
        readOnly={Boolean(data.view)}
      />
    </div>
  );
}
